'use client';

import { MatchupHeader } from '@/app/team-leagues/[teamLeagueId]/matchup/_components/MatchupHeader';
import { MatchupHeaderSkeleton } from '@/app/team-leagues/[teamLeagueId]/matchup/_components/MatchupHeaderSkeleton';
import { NoDataMessage } from '@/components/molecules/NoDataMessage';
import { TEAM_LEAGUES_MODULE } from '@/entities/matchup/constants';
import { LINEUP_PERMISSIONS, PermissionRole } from '@/entities/PermissionRoles';
import { useSeasonDivisionSettings } from '@/hooks/leagueSeasonDivision/useSeasonDivisionSettings';
import { useMatchesQuery } from '@/hooks/matches/useMatchesQuery';
import { usePodsQuery } from '@/hooks/matches/usePodsQuery';
import { useSystemLocationCourtsQuery } from '@/hooks/system-locations/useSystemLocationCourtsQuery';
import { usePermissionStore } from '@/store/permission-store';

interface TieBreakerTeamHeaderProps {
	matchupId: string;
}

export const TieBreakerTeamHeader = ({ matchupId }: TieBreakerTeamHeaderProps) => {
	const permissionsStore = usePermissionStore();
	const { data: matchesResponse, isLoading } = useMatchesQuery(matchupId);
	const { data: podsResponse, isLoading: isPodsLoading } = usePodsQuery(matchesResponse?.data?.poolUuid || '');
	const { data: poolData, isLoading: isLoadingPoolData } = useSeasonDivisionSettings(matchesResponse?.data?.poolUuid || '');
	const { data: systemLocationCourtsResponse, isLoading: isLoadingSystemLocations } = useSystemLocationCourtsQuery({
		moduleFor: TEAM_LEAGUES_MODULE.toString(),
		moduleUuid: matchesResponse?.data?.matches[0]?.moduleUuid ?? '',
		locationId: matchesResponse?.data?.locationUuid ?? ''
	});

	const isUserCaptain = permissionsStore.userPermission === PermissionRole.TEAM_CAPTAIN;

	if (isLoading || isPodsLoading || isLoadingPoolData || isLoadingSystemLocations) return <MatchupHeaderSkeleton />;

	if (!matchesResponse?.data || podsResponse?.statusCode !== 200 || !poolData) {
		return (
			<NoDataMessage title="No rally here!" description="Looks like there's nothing to show right now. Check back soon—we'll be warming up!" />
		);
	}

	const canEditLineups = permissionsStore.gamedayAccess || (isUserCaptain && LINEUP_PERMISSIONS.some((v) => poolData?.captainPermissions === v));

	return (
		<MatchupHeader
			matchup={matchesResponse.data}
			canEditLineups={canEditLineups}
			podsResponse={podsResponse?.data}
			previousPageTitle="Back"
			isLineupPage={false}
			isTieBreaker
			systemLocationsCourts={systemLocationCourtsResponse?.data}
		/>
	);
};
